/**
 * Seeded random number generator for reproducible randomization
 * Uses a simple LCG (Linear Congruential Generator) algorithm
 */

class SeededRandom {
  constructor(seed) {
    this.seed = seed % 2147483647
    if (this.seed <= 0) this.seed += 2147483646
  }

  /**
   * Generate next random number between 0 and 1
   * @returns {number} Random number
   */
  next() {
    this.seed = (this.seed * 16807) % 2147483647
    return (this.seed - 1) / 2147483646
  }

  /**
   * Generate random integer between min and max (inclusive)
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @returns {number} Random integer
   */
  nextInt(min, max) {
    return Math.floor(this.next() * (max - min + 1)) + min
  }
}

/**
 * Shuffle an array using a seeded random number generator
 * @param {Array} array - Array to shuffle
 * @param {number} seed - Seed for reproducibility
 * @returns {Array} Shuffled array
 */
export const shuffleWithSeed = (array, seed) => {
  const shuffled = [...array]
  const rng = new SeededRandom(seed)

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = rng.nextInt(0, i)
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }

  return shuffled
}

/**
 * Generate a random seed based on timestamp
 * @returns {number} Random seed
 */
export const generateSeed = () => {
  return Date.now() % 2147483647
}

/**
 * Select N random items from array with seed
 * @param {Array} array - Source array
 * @param {number} count - Number of items to select
 * @param {number} seed - Seed for reproducibility
 * @returns {Array} Selected items
 */
export const selectRandomWithSeed = (array, count, seed) => {
  const shuffled = shuffleWithSeed(array, seed)
  return shuffled.slice(0, Math.min(count, array.length))
}

/**
 * Create a deterministic question set for a test
 * @param {Array} questions - All available questions
 * @param {number} count - Number of questions to select
 * @param {number} seed - Seed for reproducibility
 * @returns {Object} { questions: Array, seed: number, order: Array }
 */
export const createTestSet = (questions, count, seed = null) => {
  const usedSeed = seed || generateSeed()
  const selectedQuestions = selectRandomWithSeed(questions, count, usedSeed)
  const order = selectedQuestions.map((q) => q.id)

  return {
    questions: selectedQuestions,
    seed: usedSeed,
    order,
    timestamp: new Date().toISOString(),
  }
}

/**
 * Recreate a test set from a previous seed and order
 * @param {Array} allQuestions - All available questions
 * @param {number} seed - Original seed
 * @param {Array} order - Original question order (IDs)
 * @returns {Array} Recreated question set
 */
export const recreateTestSet = (allQuestions, seed, order) => {
  const questionMap = {}
  allQuestions.forEach((q) => {
    questionMap[q.id] = q
  })

  return order.map((id) => questionMap[id]).filter(Boolean)
}

/**
 * Shuffle options for a multiple choice question
 * @param {Array} options - Options to shuffle
 * @param {string} questionId - Question ID for seed generation
 * @param {number} baseSeed - Base seed from test
 * @returns {Array} Shuffled options
 */
export const shuffleOptions = (options, questionId, baseSeed) => {
  // Create a unique seed for this question by combining base seed and question ID
  const questionSeed = baseSeed + questionId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return shuffleWithSeed(options, questionSeed)
}
